import { User, LoginCredentials, SignupData } from '../../types/auth';

export class AuthService {
  private baseUrl: string;
  private headers: Record<string, string>;

  constructor(config: { baseUrl: string; headers: Record<string, string> }) {
    this.baseUrl = config.baseUrl;
    this.headers = config.headers;
  }

  async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    try {
      console.log('Making login request with:', credentials);
      
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(credentials)
      });

      console.log('Got response:', response.status, response.statusText);
      
      let data;
      try {
        const text = await response.text();
        console.log('Response text:', text);
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        throw new Error(data?.message || 'Login failed');
      }

      if (!data?.user || !data?.token) {
        console.error('Invalid response data:', data);
        throw new Error('Invalid response format');
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async signup(data: SignupData): Promise<{ user: User; token: string }> {
    try {
      console.log('Making signup request with:', data);
      
      const response = await fetch(`${this.baseUrl}/auth/signup`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(data)
      });

      console.log('Got response:', response.status, response.statusText);
      
      let responseData;
      try {
        const text = await response.text();
        console.log('Response text:', text);
        responseData = text ? JSON.parse(text) : null;
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        // If the server returns validation errors array, format them
        if (responseData?.errors && Array.isArray(responseData.errors)) {
          const errorMessages = responseData.errors.map((err: any) => err.msg || err.message).join(', ');
          throw new Error(errorMessages || responseData?.message || 'Signup failed');
        }
        throw new Error(responseData?.message || 'Signup failed');
      }

      if (!responseData?.user || !responseData?.token) {
        console.error('Invalid response data:', responseData);
        throw new Error('Invalid response format');
      }

      return responseData;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/logout`, {
      method: 'POST',
      headers: this.headers
    });

    if (!response.ok) {
      throw new Error('Logout failed');
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      console.log('Fetching current user information');
      
      const response = await fetch(`${this.baseUrl}/auth/me`, {
        method: 'GET',
        headers: this.headers
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user information');
      }
      
      const userData = await response.json();
      console.log('Current user data received:', userData);
      
      return userData;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }
}